import { logger } from '@/lib/logger';
import { useState, useCallback, useMemo, useContext, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { format, isToday, isYesterday, isThisWeek } from 'date-fns';
import { ChevronRight, Trash2, MessageSquare, Pencil, MoreVertical } from 'lucide-react';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from '@/components/ui/context-menu';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { SelectedThreadContext } from '@/App';
import { SidebarContext } from '@/contexts/SidebarContext';

interface Session {
  id: string;
  title: string;
  created_at: string;
}

interface GroupedSessions {
  [key: string]: Session[];
}

interface SidebarChatThreadsProps {
  sessions: Session[];
  currentSession: string | null;
  isDesktopExpanded: boolean;
  setActiveTab: (tab: string | null) => void;
  onDeleteThread: (id: string) => void;
  onRenameThread: (id: string, newTitle: string) => void;
  onDesktopExpandedChange: (expanded: boolean) => void;
  onContextMenuOpenChange: (open: boolean) => void;
}

export const SidebarChatThreads = memo(function SidebarChatThreads({
  sessions,
  currentSession,
  isDesktopExpanded,
  setActiveTab,
  onDeleteThread,
  onRenameThread,
  onDesktopExpandedChange,
  onContextMenuOpenChange,
}: SidebarChatThreadsProps) {
  const [editingThread, setEditingThread] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const navigate = useNavigate();
  const { selectedThreadId, setSelectedThreadId } = useContext(SelectedThreadContext);
  const { setIsExpanded, isMobile } = useContext(SidebarContext);

  const handleStartEdit = (threadId: string, currentTitle: string) => {
    setEditingThread(threadId);
    setEditTitle(currentTitle);
  };

  const handleFinishEdit = async (threadId: string) => {
    if (editTitle.trim()) {
      await onRenameThread(threadId, editTitle.trim());
    }
    setEditingThread(null);
    setEditTitle('');
  };

  const groupSessionsByDate = useCallback((sessions: Session[]) => {
    const grouped: GroupedSessions = {};

    sessions.forEach((session) => {
      const date = new Date(session.created_at);
      let key = '';

      if (isToday(date)) {
        key = 'Today';
      } else if (isYesterday(date)) {
        key = 'Yesterday';
      } else if (isThisWeek(date)) {
        key = 'This Week';
      } else {
        key = format(date, 'MMMM yyyy');
      }

      if (!grouped[key]) {
        grouped[key] = [];
      }
      grouped[key].push(session);
    });

    // Sort sessions within each group by created_at descending
    Object.keys(grouped).forEach((key) => {
      grouped[key].sort(
        (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
    });

    return grouped;
  }, []);

  const groupedSessions = useMemo(
    () => groupSessionsByDate(sessions),
    [sessions, groupSessionsByDate]
  );

  const sortedSessionEntries = useMemo(() => {
    return Object.entries(groupedSessions).sort((a, b) => {
      const order = ['Today', 'Yesterday', 'This Week'];
      const aIndex = order.indexOf(a[0]);
      const bIndex = order.indexOf(b[0]);

      if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
      if (aIndex !== -1) return -1;
      if (bIndex !== -1) return 1;

      try {
        const aDate = new Date(Date.parse(`1 ${a[0]}`));
        const bDate = new Date(Date.parse(`1 ${b[0]}`));
        return bDate.getTime() - aDate.getTime();
      } catch (err) {
        logger.error('Error parsing month dates', err as Error);
        return 0;
      }
    });
  }, [groupedSessions]);

  const handleThreadClick = (threadId: string) => {
    logger.debug('Sidebar: Clicked on thread', { threadId });
    setSelectedThreadId(threadId);
    setActiveTab(threadId);
    navigate(`/chat/${threadId}`, { replace: true, state: { fromSidebar: true } });

    if (isMobile) {
      onDesktopExpandedChange(false);
      setIsExpanded(false);
    }
  };

  const handleDelete = async (threadId: string) => {
    try {
      const isCurrentThread =
        threadId === selectedThreadId ||
        threadId === currentSession ||
        window.location.pathname.includes(threadId);

      await onDeleteThread(threadId);

      if (isCurrentThread) {
        const remainingThreads = sessions.filter((s) => s.id !== threadId);
        const otherThread = remainingThreads[0];

        if (otherThread) {
          setSelectedThreadId(otherThread.id);
          setActiveTab(otherThread.id);
          navigate(`/chat/${otherThread.id}`, { replace: true });
        } else {
          setSelectedThreadId(null);
          setActiveTab(null);
          navigate('/chat', { replace: true });
        }
      }
    } catch (error) {
      logger.error('Failed to delete thread', error as Error);
    }
  };

  return (
    <div className="px-2">
      <div
        className={cn(
          'space-y-1 pb-2',
          isDesktopExpanded ? 'ml-6' : 'ml-0'
        )}
      >
        {sessions.length === 0 ? (
          isDesktopExpanded ? (
            <div className="px-3 py-2 text-xs text-muted-foreground dark:text-gray-400">
              No conversations yet
            </div>
          ) : (
            <div className="flex justify-center py-2">
              <div className="text-xs text-muted-foreground dark:text-gray-400">—</div>
            </div>
          )
        ) : (
          sortedSessionEntries.map(([date, dateSessions]) => (
            <div key={date} className="space-y-1">
              {isDesktopExpanded && (
                <h3 className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-300 px-3 py-1.5 mt-1 first:mt-0">
                  {date}
                </h3>
              )}
              {dateSessions.map((session) => (
                <ContextMenu key={session.id} onOpenChange={onContextMenuOpenChange}>
                  <ContextMenuTrigger>
                    {editingThread === session.id ? (
                      <div className="px-3 py-1">
                        <Input
                          value={editTitle}
                          onChange={(e) => setEditTitle(e.target.value)}
                          onBlur={() => handleFinishEdit(session.id)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              handleFinishEdit(session.id);
                            } else if (e.key === 'Escape') {
                              setEditingThread(null);
                              setEditTitle('');
                            }
                          }}
                          autoFocus
                          className="dark:bg-gray-700 dark:border-gray-600 h-7 text-sm"
                        />
                      </div>
                    ) : (
                      <div className="flex items-center w-full group">
                        <button
                          onClick={() => handleThreadClick(session.id)}
                          className={cn(
                            'flex-1 min-w-0 flex items-center gap-2 rounded-lg nav-item text-left min-h-[44px] md:min-h-0',
                            isDesktopExpanded ? 'px-3 py-1.5' : 'p-1.5 justify-center',
                            selectedThreadId === session.id
                              ? 'bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300'
                              : 'hover:bg-gray-100 dark:hover:bg-gray-700/30 text-gray-700 dark:text-gray-200',
                            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F37022] focus-visible:ring-offset-1'
                          )}
                        >
                          <MessageSquare
                            className={cn(
                              'w-3.5 h-3.5 shrink-0',
                              selectedThreadId === session.id &&
                                'text-[#F37022] dark:text-orange-300'
                            )}
                          />

                          {isDesktopExpanded && (
                            <span
                              className={cn(
                                'text-xs truncate flex-1 min-w-0',
                                selectedThreadId === session.id &&
                                  'font-medium text-[#F37022] dark:text-orange-300'
                              )}
                            >
                              {session.title}
                            </span>
                          )}

                          {isDesktopExpanded && selectedThreadId === session.id && (
                            <ChevronRight className="w-3.5 h-3.5 shrink-0 text-[#F37022] dark:text-orange-300" />
                          )}
                        </button>

                        {/* Kebab menu for touch-accessible rename/delete */}
                        {isDesktopExpanded && selectedThreadId === session.id && (
                          <DropdownMenu onOpenChange={onContextMenuOpenChange}>
                            <DropdownMenuTrigger asChild>
                              <button
                                className="shrink-0 p-2 min-h-[44px] md:min-h-0 flex items-center justify-center rounded-md text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F37022] focus-visible:ring-offset-1"
                                aria-label="Thread options"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <MoreVertical className="w-3.5 h-3.5" />
                              </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent
                              align="end"
                              className="dark:bg-gray-800 dark:border-gray-700"
                              collisionPadding={8}
                            >
                              <DropdownMenuItem
                                className="dark:hover:bg-gray-700 dark:text-gray-200"
                                onClick={() => handleStartEdit(session.id, session.title)}
                              >
                                <Pencil className="w-4 h-4 mr-2" />
                                Rename
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                className="text-destructive dark:text-red-400 dark:hover:bg-gray-700"
                                onClick={() => handleDelete(session.id)}
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </div>
                    )}
                  </ContextMenuTrigger>
                  <ContextMenuContent className="dark:bg-gray-800 dark:border-gray-700">
                    <ContextMenuItem
                      className="dark:hover:bg-gray-700 dark:text-gray-200"
                      onClick={() => handleStartEdit(session.id, session.title)}
                    >
                      <Pencil className="w-4 h-4 mr-2" />
                      Rename
                    </ContextMenuItem>
                    <ContextMenuItem
                      className="text-destructive dark:text-red-400 dark:hover:bg-gray-700"
                      onClick={() => handleDelete(session.id)}
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete
                    </ContextMenuItem>
                  </ContextMenuContent>
                </ContextMenu>
              ))}
            </div>
          ))
        )}
      </div>
    </div>
  );
});
